import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { 
  ChevronRight, ChevronDown, Package, Truck, MapPin, Calendar,
  Clock, Loader2, RefreshCw, ExternalLink, FileText, Edit, Trash2,
  AlertTriangle, Eye, ArrowLeftRight
} from 'lucide-react';
import BulkStatusChangeModal from '../BulkStatusChangeModal';
import { cn } from '../../lib/utils';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configuration des statuts
const TRANSFER_STATUS_CONFIG = {
  planifiee: { label: 'Planifié', dotColor: 'bg-slate-400' },
  planned: { label: 'Planifié', dotColor: 'bg-slate-400' },
  en_cours_de_chargement: { label: 'Chargement', dotColor: 'bg-blue-400' },
  en_transit: { label: 'En transit', dotColor: 'bg-amber-400' },
  en_cours_de_dechargement: { label: 'Déchargement', dotColor: 'bg-purple-400' },
  in_progress: { label: 'En cours', dotColor: 'bg-blue-400' },
  completed: { label: 'Terminé', dotColor: 'bg-emerald-400' },
  cancelled: { label: 'Annulé', dotColor: 'bg-red-400' }
};

/**
 * HierarchyView - Vue hiérarchique Contrat → Opération → Transferts
 * Avec actions (Modifier/Supprimer) et sélection en masse
 */
const HierarchyView = ({ contractId, contractNumber, runs: initialRuns, onRefresh, onEditTransfer, readOnly = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(!initialRuns);
  const [runs, setRuns] = useState(initialRuns || []);
  const [operations, setOperations] = useState([]);
  const [expandedOperations, setExpandedOperations] = useState(new Set());
  
  // Sélection
  const [selectedTransfers, setSelectedTransfers] = useState(new Set());
  
  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transferToDelete, setTransferToDelete] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);

  // Charger les données si pas fournies
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const runsResponse = await axios.get(`${API}/contract-runs?contract_id=${contractId}&limit=200`);
      const runsData = runsResponse.data.items || runsResponse.data || [];
      setRuns(runsData);

      const parentIds = [...new Set(runsData.map(r => r.parentOperationId).filter(Boolean))];
      
      if (parentIds.length > 0) {
        const opsPromises = parentIds.map(id => 
          axios.get(`${API}/transport-requests/${id}`).catch(() => null)
        );
        const opsResponses = await Promise.all(opsPromises);
        const opsData = opsResponses
          .filter(r => r?.data)
          .map(r => r.data);
        setOperations(opsData);
      }
    } catch (error) {
      console.error('Error fetching hierarchy data:', error);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    if (!initialRuns) {
      fetchData();
    }
  }, [fetchData, initialRuns]);

  useEffect(() => {
    if (initialRuns) {
      setRuns(initialRuns);
      const parentIds = [...new Set(initialRuns.map(r => r.parentOperationId).filter(Boolean))];
      if (parentIds.length > 0) {
        Promise.all(
          parentIds.map(id => axios.get(`${API}/transport-requests/${id}`).catch(() => null))
        ).then(responses => {
          const opsData = responses.filter(r => r?.data).map(r => r.data);
          setOperations(opsData);
        });
      }
    }
  }, [initialRuns]);

  // Regrouper les transferts par opération mère
  const hierarchy = useMemo(() => {
    const grouped = {};
    const orphans = [];

    runs.forEach(run => {
      if (run.parentOperationId) {
        if (!grouped[run.parentOperationId]) {
          grouped[run.parentOperationId] = [];
        }
        grouped[run.parentOperationId].push(run);
      } else {
        orphans.push(run);
      }
    });

    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => new Date(a.date) - new Date(b.date));
    });
    orphans.sort((a, b) => new Date(a.date) - new Date(b.date));

    return { grouped, orphans };
  }, [runs]);

  const toggleOperation = (opId) => {
    setExpandedOperations(prev => {
      const next = new Set(prev);
      if (next.has(opId)) {
        next.delete(opId);
      } else {
        next.add(opId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedOperations(new Set(Object.keys(hierarchy.grouped)));
  };

  const collapseAll = () => {
    setExpandedOperations(new Set());
  };

  // Sélection
  const toggleTransferSelection = (transferId) => {
    setSelectedTransfers(prev => {
      const next = new Set(prev);
      if (next.has(transferId)) {
        next.delete(transferId);
      } else {
        next.add(transferId);
      }
      return next;
    });
  };

  const selectAllInOperation = (opId, transfers) => {
    setSelectedTransfers(prev => {
      const next = new Set(prev);
      const allSelected = transfers.every(t => prev.has(t.id));
      if (allSelected) {
        transfers.forEach(t => next.delete(t.id));
      } else {
        transfers.forEach(t => next.add(t.id));
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedTransfers.size === runs.length) {
      setSelectedTransfers(new Set());
    } else {
      setSelectedTransfers(new Set(runs.map(r => r.id)));
    }
  };

  const clearSelection = () => {
    setSelectedTransfers(new Set());
  };

  // Actions
  const handleViewTransfer = (transfer, e) => {
    e?.stopPropagation();
    if (readOnly) {
      navigate(`/mes-contrats/${contractId}/transferts/${transfer.id}`);
    } else {
      navigate(`/admin/transfers/${transfer.id}`);
    }
  };

  const handleEditTransfer = (transfer, e) => {
    e?.stopPropagation();
    if (onEditTransfer) {
      onEditTransfer(transfer);
    } else {
      // Naviguer vers la page de détail du transfert (pas de page d'édition séparée)
      navigate(`/admin/transfers/${transfer.id}`);
    }
  };

  const handleDeleteClick = (transfer, e) => {
    e?.stopPropagation();
    setTransferToDelete(transfer);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!transferToDelete) return;
    
    setDeleting(true);
    try {
      const response = await axios.post(`${API}/contract-runs/bulk-delete`, {
        run_ids: [transferToDelete.id],
        force: isAdmin  // Admin peut supprimer n'importe quel statut
      });
      
      const result = response.data;
      if (result.deleted > 0) {
        toast({
          title: "Transfert supprimé",
          description: `Le transfert ${transferToDelete.runNumber || transferToDelete.id.slice(0, 8)} a été supprimé.`,
        });
      } else if (result.skipped_details?.length > 0) {
        const reason = result.skipped_details[0]?.reason || "Statut non supprimable";
        toast({
          title: "Suppression impossible",
          description: reason,
          variant: "destructive"
        });
      }
      
      // Rafraîchir les données
      if (onRefresh) {
        onRefresh();
      } else {
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting transfer:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de supprimer le transfert",
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setTransferToDelete(null);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedTransfers.size === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    setDeleting(true);
    const idsToDelete = Array.from(selectedTransfers);

    try {
      // Utiliser l'endpoint bulk-delete avec POST
      const response = await axios.post(`${API}/contract-runs/bulk-delete`, {
        run_ids: idsToDelete,
        force: isAdmin  // Admin peut supprimer n'importe quel statut
      });
      
      const result = response.data;
      const successCount = result.deleted || 0;
      const errorCount = result.skipped || 0;
      
      // Construire le message de détail si des transferts n'ont pas pu être supprimés
      let description = `${successCount} transfert(s) supprimé(s)`;
      if (errorCount > 0 && result.skipped_details?.length > 0) {
        const reasons = [...new Set(result.skipped_details.map(s => s.reason))];
        description += `. ${errorCount} non supprimé(s): ${reasons.join(', ')}`;
      }

      toast({
        title: "Suppression terminée",
        description,
        variant: errorCount > 0 && successCount === 0 ? "destructive" : (errorCount > 0 ? "warning" : "default")
      });

      setSelectedTransfers(new Set());
      if (onRefresh) {
        onRefresh();
      } else {
        fetchData();
      }
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue lors de la suppression",
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusDot = (status) => {
    const config = TRANSFER_STATUS_CONFIG[status] || TRANSFER_STATUS_CONFIG.planifiee;
    return (
      <span className="flex items-center gap-1.5 text-xs text-gray-600">
        <span className={cn("w-2 h-2 rounded-full", config.dotColor)} />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Chargement de la hiérarchie...</span>
      </div>
    );
  }

  const operationsCount = Object.keys(hierarchy.grouped).length;
  const transfersCount = runs.length;

  return (
    <div className="space-y-4" data-testid="hierarchy-view">
      {/* Header avec stats et actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-1.5">
            <Package className="w-4 h-4" />
            {operationsCount} opération{operationsCount > 1 ? 's' : ''}
          </span>
          <span className="flex items-center gap-1.5">
            <Truck className="w-4 h-4" />
            {transfersCount} transfert{transfersCount > 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={expandAll}>
            Tout déplier
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            Tout replier
          </Button>
          <Button variant="outline" size="sm" onClick={onRefresh || fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Barre d'actions en masse */}
      {!readOnly && selectedTransfers.size > 0 && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="bulk-actions-bar">
          <div className="flex items-center gap-3">
            <Checkbox 
              checked={selectedTransfers.size === runs.length}
              onCheckedChange={selectAll}
            />
            <span className="text-sm font-medium text-blue-700">
              {selectedTransfers.size} transfert{selectedTransfers.size > 1 ? 's' : ''} sélectionné{selectedTransfers.size > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBulkStatusModal(true)}
              className="border-blue-300 text-blue-700 hover:bg-blue-100"
              data-testid="bulk-change-status-btn-hierarchy"
            >
              <ArrowLeftRight className="w-4 h-4 mr-1" />
              Changer statut ({selectedTransfers.size})
            </Button>
            <Button variant="outline" size="sm" onClick={clearSelection}>
              Désélectionner tout
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={handleBulkDeleteClick}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Supprimer ({selectedTransfers.size})
            </Button>
          </div>
        </div>
      )}

      {/* Vue hiérarchique */}
      <div className="space-y-3">
        {/* Opérations groupées */}
        {Object.entries(hierarchy.grouped).map(([opId, transfers]) => {
          const operation = operations.find(op => op.id === opId);
          const isExpanded = expandedOperations.has(opId);
          const allSelected = transfers.every(t => selectedTransfers.has(t.id));
          const someSelected = transfers.some(t => selectedTransfers.has(t.id));
          
          const statusCounts = transfers.reduce((acc, t) => {
            const status = t.status || 'planifiee';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          }, {});

          return (
            <Card key={opId} className="overflow-hidden" data-testid={`operation-group-${opId}`}>
              {/* En-tête de l'opération */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleOperation(opId)}
              >
                <div className="flex items-center gap-3">
                  {/* Checkbox pour sélection groupée */}
                  {!readOnly && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox 
                      checked={allSelected}
                      className={someSelected && !allSelected ? "data-[state=checked]:bg-blue-400" : ""}
                      onCheckedChange={() => selectAllInOperation(opId, transfers)}
                      data-testid={`select-operation-${opId}`}
                    />
                  </div>
                  )}
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                  <Package className="w-5 h-5 text-emerald-600" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium text-gray-700">
                        {operation?.operationNumber || `OP-${opId.slice(0, 8)}`}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {transfers.length} transfert{transfers.length > 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                      <MapPin className="w-3 h-3 text-blue-500" />
                      <span className="truncate max-w-[200px]">
                        {operation?.departure || transfers[0]?.stops?.[0]?.address || 'N/A'}
                      </span>
                      <span className="text-gray-400 mx-1">→</span>
                      <MapPin className="w-3 h-3 text-emerald-500" />
                      <span className="truncate max-w-[200px]">
                        {operation?.arrival || transfers[0]?.stops?.[1]?.address || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Stats des statuts */}
                <div className="flex items-center gap-3">
                  {Object.entries(statusCounts).map(([status, count]) => (
                    <span key={status} className="flex items-center gap-1 text-xs">
                      <span className={cn("w-2 h-2 rounded-full", TRANSFER_STATUS_CONFIG[status]?.dotColor || 'bg-gray-400')} />
                      {count}
                    </span>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (readOnly) {
                        navigate(`/operations/${opId}`);
                      } else {
                        navigate(`/admin/operations/${opId}`);
                      }
                    }}
                    title="Voir l'opération"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Liste des transferts (expandable) */}
              {isExpanded && (
                <div className="border-t bg-gray-50/50">
                  {transfers.map((transfer, idx) => (
                    <div
                      key={transfer.id}
                      className={cn(
                        "flex items-center justify-between px-4 py-3 border-b last:border-b-0 hover:bg-white transition-colors",
                        selectedTransfers.has(transfer.id) && "bg-blue-50"
                      )}
                      data-testid={`transfer-item-${transfer.id}`}
                    >
                      <div className="flex items-center gap-3 ml-4">
                        {/* Checkbox de sélection */}
                        {!readOnly && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox 
                            checked={selectedTransfers.has(transfer.id)}
                            onCheckedChange={() => toggleTransferSelection(transfer.id)}
                            data-testid={`select-transfer-${transfer.id}`}
                          />
                        </div>
                        )}
                        <div className="w-6 h-6 flex items-center justify-center">
                          {idx === transfers.length - 1 ? (
                            <span className="w-px h-3 bg-gray-300" />
                          ) : (
                            <span className="w-px h-6 bg-gray-300" />
                          )}
                        </div>
                        <Truck className="w-4 h-4 text-gray-400" />
                        <div>
                          <span className="font-mono text-sm text-gray-600" title={transfer.runNumber || ''}>
                            {transfer.shortReference || transfer.runNumber || `TRF-${transfer.id.slice(0, 8)}`}
                          </span>
                          {transfer.shortReference && transfer.runNumber && (
                            <div className="text-xs text-gray-400 truncate max-w-[180px]" title={transfer.runNumber}>
                              {transfer.runNumber}
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                            <Calendar className="w-3 h-3" />
                            {formatDate(transfer.date)}
                            <span className="text-gray-300">|</span>
                            <Clock className="w-3 h-3" />
                            {transfer.shift === 'day' ? 'Jour' : 'Nuit'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Véhicule/Chauffeur */}
                        <div className="text-xs text-gray-500 text-right">
                          <div>{transfer.plannedVehiclePlate || '-'}</div>
                          <div>{transfer.plannedDriverName || 'Non assigné'}</div>
                        </div>
                        {getStatusDot(transfer.status)}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => handleViewTransfer(transfer, e)}
                            title="Voir"
                            data-testid={`view-transfer-${transfer.id}`}
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </Button>
                          {!readOnly && (
                          <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => handleEditTransfer(transfer, e)}
                            title="Modifier"
                            data-testid={`edit-transfer-${transfer.id}`}
                          >
                            <Edit className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => handleDeleteClick(transfer, e)}
                            title="Supprimer"
                            data-testid={`delete-transfer-${transfer.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                          </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}

        {/* Transferts orphelins */}
        {hierarchy.orphans.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                <FileText className="w-4 h-4" />
                Transferts sans opération mère ({hierarchy.orphans.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {hierarchy.orphans.map(transfer => (
                  <div
                    key={transfer.id}
                    className={cn(
                      "flex items-center justify-between p-3 bg-white rounded border hover:border-amber-300 transition-colors",
                      selectedTransfers.has(transfer.id) && "bg-blue-50 border-blue-300"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {!readOnly && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedTransfers.has(transfer.id)}
                          onCheckedChange={() => toggleTransferSelection(transfer.id)}
                        />
                      </div>
                      )}
                      <Truck className="w-4 h-4 text-amber-500" />
                      <div>
                        <span className="font-mono text-sm text-gray-600">
                          {transfer.runNumber || `TRF-${transfer.id.slice(0, 8)}`}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          <Calendar className="w-3 h-3" />
                          {formatDate(transfer.date)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {getStatusDot(transfer.status)}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => handleViewTransfer(transfer, e)}
                          title="Voir"
                        >
                          <Eye className="w-4 h-4 text-gray-500" />
                        </Button>
                        {!readOnly && (
                        <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => handleEditTransfer(transfer, e)}
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => handleDeleteClick(transfer, e)}
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                        </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* État vide */}
        {operationsCount === 0 && hierarchy.orphans.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucune opération ou transfert pour ce contrat</p>
          </div>
        )}
      </div>

      {/* Dialog de confirmation suppression simple */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Confirmer la suppression
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>Êtes-vous sûr de vouloir supprimer le transfert{' '}
                <strong>{transferToDelete?.runNumber || transferToDelete?.id?.slice(0, 8)}</strong> ?</p>
                {transferToDelete?.status === 'completed' && isAdmin && (
                  <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-orange-700 text-sm">
                    ⚠️ Ce transfert est <strong>Terminé</strong>. La suppression est irréversible et sera tracée dans l'audit.
                  </div>
                )}
                <p className="mt-1 text-sm">Cette action est irréversible.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Suppression...
                </>
              ) : (
                'Supprimer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de confirmation suppression en masse */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Suppression en masse
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>Êtes-vous sûr de vouloir supprimer <strong>{selectedTransfers.size} transfert{selectedTransfers.size > 1 ? 's' : ''}</strong> ?</p>
                {isAdmin && (() => {
                  const completedCount = runs.filter(r => selectedTransfers.has(r.id) && r.status === 'completed').length;
                  return completedCount > 0 ? (
                    <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-orange-700 text-sm">
                      ⚠️ <strong>{completedCount}</strong> transfert{completedCount > 1 ? 's' : ''} Terminé{completedCount > 1 ? 's' : ''} sera{completedCount > 1 ? 'ont' : ''} supprimé{completedCount > 1 ? 's' : ''} définitivement.
                    </div>
                  ) : null;
                })()}
                <p className="mt-1 text-sm">Cette action est irréversible.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmBulkDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Suppression...
                </>
              ) : (
                `Supprimer ${selectedTransfers.size} transfert${selectedTransfers.size > 1 ? 's' : ''}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modale changement de statut en masse */}
      <BulkStatusChangeModal
        open={showBulkStatusModal}
        onClose={() => setShowBulkStatusModal(false)}
        runIds={Array.from(selectedTransfers)}
        onSuccess={() => {
          setSelectedTransfers(new Set());
          if (onRefresh) onRefresh(); else fetchData();
        }}
      />
    </div>
  );
};

export default HierarchyView;
